import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, DialogContent, Divider, IconButton, Tooltip, Typography } from "@mui/material";
import { jsDateToDateAndTimeInTz, serializeDateTime } from "@notemeal/utils-date-time";
import { DurationSelectType, TimeDurationSelectSchema } from "apps/web/src/componentLibrary/DurationSelect/FormSchema";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import { ActivityType, MealType, useEditMealPlanCalendarMutation, useMealPlanCalendarQuery } from "apps/web/src/types";
import addMinutes from "date-fns/addMinutes";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useClientTimezone } from "shared-ui/contexts/ClientTimezone";
import { evictMealPlanCalendarQueriesForAthlete } from "./Calendar/cache";
import { MEAL, NewEventFormFields } from "./Calendar/Event/Popover/NewEvent/NewEventFormFields";
import { initEditMealPlanCalendarState } from "./Calendar/Form/reducer";
import {
  createActivityTemplateTimeFragmentFromEvent,
  createMealTemplateTimeFragmentFromEvent,
  getEditMealPlanCalendarInput,
} from "./Calendar/Form/utils";

const DEFAULT_DURATION_IN_MINUTES = 30;

interface QuickAddMealPlanEventDialogProps {
  athleteId: string;
  mealPlanId: string;
  mealPlanName: string;
  onClose: () => void;
}

export const QuickAddMealPlanEventDialog = ({ athleteId, mealPlanId, mealPlanName, onClose }: QuickAddMealPlanEventDialogProps) => {
  const clientTimezone = useClientTimezone();
  const { setMessage } = useSnackbar();
  const defaultStartTime = getDefaultStartTime();
  const [selectedTab, setSelectedTab] = useState(MEAL);
  const [mealType, setMealType] = useState<MealType>("breakfast");
  const [activityType, setActivityType] = useState<ActivityType>("practice");
  const [eventName, setEventName] = useState("");

  const form = useForm<DurationSelectType>({
    defaultValues: {
      start: defaultStartTime,
      end: addMinutes(defaultStartTime, DEFAULT_DURATION_IN_MINUTES),
    },
    resolver: zodResolver(TimeDurationSelectSchema()),
  });

  const after = jsDateToDateAndTimeInTz(new Date(), clientTimezone).date;

  const { data, loading } = useMealPlanCalendarQuery({
    variables: {
      mealPlanId,
      after,
    },
    onError: () => {
      setMessage("error", "An error occurred while loading the meal plan.");
      onClose();
    },
  });

  const [editMealPlanCalendar, { loading: saving }] = useEditMealPlanCalendarMutation({
    update: (cache, { data: mutationData }) => {
      if (mutationData) {
        evictMealPlanCalendarQueriesForAthlete({
          athleteId,
          cache,
          type: "Edit",
          mealPlanId,
        });
      }
    },
    onCompleted: () => {
      setMessage("success", "Meal plan calendar updated!");
      onClose();
    },
    onError: () => {
      setMessage("error", "An error occurred while updating the meal plan calendar. Please try again.");
    },
  });

  const isLoadingCalendar = loading && !data;
  const hasFormErrors = form.formState.isSubmitted && !form.formState.isValid;
  const saveDisabled = saving || isLoadingCalendar;
  const saveTooltipTitle = (() => {
    if (saving) {
      return "";
    }
    if (isLoadingCalendar) {
      return "Meal plan is still loading";
    }
    if (hasFormErrors) {
      return "Resolve the errors above to continue";
    }
    return "";
  })();

  const handleSave = (duration: DurationSelectType) => {
    if (!data || saving || isLoadingCalendar) {
      return;
    }

    const name = eventName.trim() || (selectedTab === MEAL ? mealType : activityType);
    const eventBase = {
      name,
      start: serializeDateTime(duration.start),
      end: serializeDateTime(duration.end),
    };

    const nextState = initEditMealPlanCalendarState({
      athleteId,
      mealPlanId,
      mealPlanName,
      clientTimezone,
      isAutoSuggestionsEnabled: data.mealPlan.isAutoSuggestionsEnabled,
      schedule: data.mealPlan.schedule,
      startDate: data.mealPlan.startDate,
      endDate: data.mealPlan.endDate,
      mealTemplates: data.mealPlan.mealTemplates,
      activityTemplates: data.mealPlan.activityTemplates,
      dayOfWeekAssignments: data.mealPlan.daysOfWeek,
    });

    const stateWithEvent =
      selectedTab === MEAL
        ? {
            ...nextState,
            mealTemplates: nextState.mealTemplates.concat({
              ...createMealTemplateTimeFragmentFromEvent({
                ...eventBase,
                type: mealType,
                __typename: "Meal",
              }),
              hasDateModifications: false,
              resetModifications: false,
              dateModifications: [],
              dayOfWeekModifications: [],
            }),
          }
        : {
            ...nextState,
            activityTemplates: nextState.activityTemplates.concat({
              ...createActivityTemplateTimeFragmentFromEvent({
                ...eventBase,
                type: activityType,
                __typename: "Activity",
              }),
              hasDateModifications: false,
              resetModifications: false,
              dateModifications: [],
              dayOfWeekModifications: [],
            }),
          };

    editMealPlanCalendar({
      variables: {
        input: getEditMealPlanCalendarInput(stateWithEvent, data.mealPlan.mealTemplates, data.mealPlan.activityTemplates, []),
      },
    });
  };

  return (
    <Dialog
      open
      onClose={saving ? () => {} : onClose}
      maxWidth={false}
      PaperProps={{
        elevation: 24,
        sx: {
          width: 470,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, py: 2 }}>
        <Typography variant="h3">Add Event</Typography>
        <IconButton onClick={onClose} disabled={saving} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ px: 3, pt: 0, pb: 0 }}>
        <NewEventFormFields
          selectedTab={selectedTab}
          onSelectedTab={setSelectedTab}
          eventName={eventName}
          onEventNameChange={setEventName}
          mealType={mealType}
          onMealTypeChange={setMealType}
          activityType={activityType}
          onActivityTypeChange={setActivityType}
          control={form.control}
          errors={form.formState.errors}
        />
      </DialogContent>
      <Divider />
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, px: 3, py: 3 }}>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        {saveTooltipTitle ? (
          <Tooltip title={saveTooltipTitle}>
            <span>
              <Button onClick={form.handleSubmit(handleSave)} disabled={saveDisabled}>
                Save
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button onClick={form.handleSubmit(handleSave)} disabled={saveDisabled}>
            Save
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

const getDefaultStartTime = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() < 30 ? 30 : 0, 0, 0);
  if (now.getMinutes() === 0) {
    now.setHours(now.getHours() + 1);
  }
  return now;
};
